import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';

/**
 * Converts a category name to a translation key
 * e.g., "Phones & Tablets" -> "phones_tablets"
 *       "phones_tablets" -> "phones_tablets"
 *       "Clothing - Men" -> "clothing_men"
 */
function toCategoryKey(categoryName: string): string {
  return categoryName
    .toLowerCase()
    .replace(/[&-]/g, '_')      // Replace & and - with _
    .replace(/\s+/g, '_')        // Replace spaces with _
    .replace(/_+/g, '_')         // Replace multiple _ with single _
    .replace(/^_|_$/g, '');      // Remove leading/trailing _
}

/**
 * Hook to translate category names
 * Returns a function that translates a category name to the user's language
 */
export function useCategoryTranslation() {
  const { t } = useTranslation();

  const translateCategory = useCallback(
    (categoryName: string | undefined | null): string => {
      if (!categoryName) return '';

      const key = toCategoryKey(categoryName);
      const translationKey = `categories.${key}`;
      const translated = t(translationKey, { defaultValue: '' });

      // If translation exists and is different from the key, use it
      // Otherwise fall back to the original name
      if (translated && translated !== translationKey && translated !== '') {
        return translated;
      }

      return categoryName;
    },
    [t]
  );

  return { translateCategory };
}

/**
 * Standalone function to translate category (for use outside React components)
 * @param categoryName - The category name to translate
 * @param t - The translation function from useTranslation
 */
export function translateCategoryName(
  categoryName: string | undefined | null,
  t: (key: string, options?: { defaultValue?: string }) => string
): string {
  if (!categoryName) return '';

  const key = toCategoryKey(categoryName);
  const translationKey = `categories.${key}`;
  const translated = t(translationKey, { defaultValue: '' });

  if (translated && translated !== translationKey && translated !== '') {
    return translated;
  }

  return categoryName;
}
